import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import { Building2, Loader2, UserRound } from 'lucide-react';
import { cn } from '../../../../shared';
import { VariableTextarea } from '../../components/VariableTextarea';
import type { InviteEmail } from '../../services/interviewLinksApi';
import {
  TOKENS_BY_CATEGORY,
  fetchEmailTemplates,
  type EmailTemplate,
} from '../../services/emailTemplatesApi';

type Lang = 'de' | 'en';

/**
 * Which Invite template a stage follows. Keyed by Lever stage id → the template
 * `key` under the "invite" category. Stages without an entry (Shortlist, BRYQ…)
 * have no Library default — a recruiter can still customise from any template.
 *
 * ⚠️ Mirrored on the proxy as INVITE_TEMPLATE_BY_STAGE in lib/emailTemplates.js,
 * which decides what is SENT for a link without custom wording. This copy decides
 * what the editor SHOWS as "the Library template this link follows". Keep the two
 * identical.
 */
const STAGE_TO_TEMPLATE: Record<string, string> = {
  'c59740e3-36e4-4263-8cde-5fe65ef308b4': 'pre-screen', // First Interview
  '4becd2d2-73a4-4b5e-a145-80c07e39849a': 'personal', // Personal Interview
  '27f97232-81dc-402a-aa54-aaf18607f3e0': 'case', // Case / Technical Interview
  '224c16e6-7210-4344-8a81-e51dba633b90': 'final-online', // Final & Bar Raiser
};

/** A deliberate customisation, started from one Library template's whole email. */
function customFromTemplate(t: EmailTemplate): InviteEmail {
  return {
    custom: true,
    templateKey: t.key,
    blockKeys: [t.id], // which template this copy started from (traceability)
    de: { subject: t.subjectDe, body: t.de },
    en: { subject: t.subjectEn, body: t.en },
  };
}

/**
 * The per-link "invite to next interview" email (Theresa, Oct 14 2026).
 *
 * By DEFAULT a link follows the Email Library's template for its stage, live: it
 * stores nothing, and a Library edit reaches every invite from it. Only the
 * deliberate "Customise wording for this link" stores the link's own text, which
 * then goes out for every invite from this link until "Back to Email Library".
 * Edits in the invite pop-up are a third, separate thing: one send only.
 *
 * ⚠️ There is deliberately NO auto-fill when a stage is picked. That auto-fill
 * froze a copy of the Library into every link, which is why Library updates never
 * reached candidates. A value without `custom: true` (an old copy) is shown as
 * "follows the Library" — the proxy ignores it and drops it on save.
 */
export function InviteEmailEditor({
  value,
  onChange,
  stageId = '',
}: {
  value: InviteEmail | null;
  onChange: (next: InviteEmail | null) => void;
  /** The picked interview stage — decides which Library template the link follows. */
  stageId?: string;
}) {
  const [lang, setLang] = useState<Lang>('de');
  const { data: library, isPending } = useQuery({
    queryKey: ['email-templates'],
    queryFn: fetchEmailTemplates,
  });

  const inviteTemplates = useMemo(
    () =>
      (library ?? [])
        .filter((t) => t.category === 'invite' && t.active !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [library]
  );

  const stageKey = STAGE_TO_TEMPLATE[stageId];
  const stageTemplate = stageKey
    ? inviteTemplates.find((t) => t.key === stageKey && t.scope === 'company')
    : undefined;

  const custom = value?.custom === true ? value : null;

  function customise(t: EmailTemplate) {
    onChange(customFromTemplate(t));
  }

  function pickTemplate(id: string) {
    const t = inviteTemplates.find((x) => x.id === id);
    if (!t) return;
    if (
      custom &&
      !window.confirm('Load this template? It replaces the current subject and body in both languages.')
    ) {
      return;
    }
    customise(t);
  }

  function backToLibrary() {
    if (
      !window.confirm(
        'Go back to the Email Library wording? Your custom text for this link is removed, and every invite from this link follows the Library again.'
      )
    ) {
      return;
    }
    onChange(null);
  }

  const setSubject = (v: string) => custom && onChange({ ...custom, [lang]: { ...custom[lang], subject: v } });
  const setBody = (v: string) => custom && onChange({ ...custom, [lang]: { ...custom[lang], body: v } });

  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Invite to next interview — email</h2>
          <p className="mt-1 text-xs text-gray-500">
            Pre-fills the invite pop-up on the TA Action Board, with the booking link and interviewer
            names filled in per candidate. Changes made in the pop-up apply to that one email only.
          </p>
        </div>
        <div className="flex overflow-hidden rounded-full border border-gray-200 text-xs">
          {(['de', 'en'] as Lang[]).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLang(l)}
              className={cn(
                'px-3.5 py-1.5 font-medium transition',
                lang === l ? 'bg-[#0072EA] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              )}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {isPending ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading templates…
        </div>
      ) : custom ? (
        <CustomWording
          invite={custom}
          lang={lang}
          templates={inviteTemplates}
          onPick={pickTemplate}
          onBack={backToLibrary}
          onSubject={setSubject}
          onBody={setBody}
        />
      ) : (
        <LibraryWording
          stageTemplate={stageTemplate}
          hasStage={!!stageId}
          lang={lang}
          templates={inviteTemplates}
          onCustomise={customise}
          onPick={pickTemplate}
        />
      )}
    </section>
  );
}

/** The default: this link follows the Library's stage template, shown read-only. */
function LibraryWording({
  stageTemplate,
  hasStage,
  lang,
  templates,
  onCustomise,
  onPick,
}: {
  stageTemplate: EmailTemplate | undefined;
  hasStage: boolean;
  lang: Lang;
  templates: EmailTemplate[];
  onCustomise: (t: EmailTemplate) => void;
  onPick: (id: string) => void;
}) {
  if (!stageTemplate) {
    return (
      <div className="mt-3 space-y-2" data-testid="invite-no-library-template">
        <p className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-xs text-gray-500">
          {hasStage
            ? 'This stage has no Email Library invite template, so a generic text is used. To write this link’s own wording, start from a template:'
            : 'Pick an interview stage above to see which Email Library template this link follows.'}
        </p>
        {hasStage && templates.length > 0 && <TemplatePicker templates={templates} onPick={onPick} />}
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-2" data-testid="invite-follows-library">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className="rounded-full px-2.5 py-1 text-xs font-semibold"
          style={{ backgroundColor: '#DCF5E4', color: '#136C34' }}
        >
          Uses Email Library template: {stageTemplate.label}
        </span>
        <Button type="button" size="sm" variant="secondary" onClick={() => onCustomise(stageTemplate)}>
          Customise wording for this link
        </Button>
      </div>
      <p className="text-xs text-gray-500">
        Updates to this template in the Email Library reach every invite from this link automatically.
      </p>
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 text-[13px] text-gray-700">
        <p className="font-medium">{lang === 'de' ? stageTemplate.subjectDe : stageTemplate.subjectEn}</p>
        <p className="mt-2 whitespace-pre-wrap font-mono text-[12px]">
          {lang === 'de' ? stageTemplate.de : stageTemplate.en}
        </p>
      </div>
    </div>
  );
}

/** A deliberate customisation: editable, labelled, and reversible. */
function CustomWording({
  invite,
  lang,
  templates,
  onPick,
  onBack,
  onSubject,
  onBody,
}: {
  invite: InviteEmail;
  lang: Lang;
  templates: EmailTemplate[];
  onPick: (id: string) => void;
  onBack: () => void;
  onSubject: (v: string) => void;
  onBody: (v: string) => void;
}) {
  return (
    <div className="mt-3 space-y-2" data-testid="invite-custom-wording">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className="rounded-full px-2.5 py-1 text-xs font-semibold"
          style={{ backgroundColor: '#FFF1DB', color: '#8A4B00' }}
        >
          Custom wording for this link (not following the Email Library)
        </span>
        <Button type="button" size="sm" variant="ghost" onClick={onBack}>
          Back to Email Library
        </Button>
      </div>
      <p className="text-xs text-gray-500">
        Once saved, this text goes out for every invite from this link, even if the Library changes.
      </p>
      {templates.length > 0 && (
        <TemplatePicker templates={templates} value={invite.blockKeys?.[0] || ''} onPick={onPick} />
      )}
      <Input
        label="Subject"
        value={invite[lang].subject}
        onChange={(e) => onSubject((e.target as HTMLInputElement).value)}
        className="rounded-xl"
      />
      <VariableTextarea
        label={`Email body (${lang.toUpperCase()})`}
        tokens={TOKENS_BY_CATEGORY.invite}
        rows={18}
        value={invite[lang].body}
        placeholder="Write this link’s invite here."
        onChange={onBody}
        textareaClassName="min-h-[26rem] font-mono text-[13px]"
      />
    </div>
  );
}

function TemplatePicker({
  templates,
  value = '',
  onPick,
}: {
  templates: EmailTemplate[];
  value?: string;
  onPick: (id: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-700">Start from template</span>
      <Select value={value || undefined} onValueChange={onPick}>
        <SelectTrigger className="w-full rounded-xl">
          <SelectValue placeholder="Choose an invite template" />
        </SelectTrigger>
        <SelectContent>
          {templates.map((t) => (
            <SelectItem key={t.id} value={t.id}>
              <span className="inline-flex items-center gap-1.5">
                {t.scope === 'personal' ? (
                  <UserRound className="h-3 w-3 text-purple-600" />
                ) : (
                  <Building2 className="h-3 w-3 text-blue-600" />
                )}
                {t.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}
